import { useEffect, useState, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { fmtDateTime } from "../lib/tz";
import { PageHeader, ActionButton, GhostButton, PillBadge, FormField, inputClass } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { SingleSelectDropdown } from "../components/SingleSelectDropdown";
import PhotoAttachmentsField from "../components/PhotoAttachmentsField";
import AttachmentViewer from "../components/AttachmentViewer";
import ResendReportEmailButton from "../components/ResendReportEmailButton";
import BulkResendBar from "../components/BulkResendBar";
import useBulkSelection from "../hooks/useBulkSelection";
import StickyFiltersBar, { useFullscreenRoute } from "../components/StickyFiltersBar";
import { useRememberWindowGeometry } from "../lib/popoutGeometry";
import { Plus, Trash2 } from "lucide-react";
import { AdminCommentsButton } from "../components/AdminCommentsPanel";
import ApprovalActions, { ApprovalPill } from "../components/ApprovalActions";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const empty = {
  site_id: "",
  guard_id: "",
  title: "",
  description: "",
  category: "other",
  severity: "low",
  status: null,   // iter238 — supervisor-only; null until explicitly set
  location_on_site: "",
  action_taken: "",
  follow_up_needed: false,
  photo_attachments: [],
};

export default function Maintenance() {
  const { user } = useAuth();
  // iter238 — status is a supervisor-and-above field.
  const canEditStatus = ["supervisor", "admin", "super_admin", "chief_security_officer",
                          "operations_manager", "dispatcher", "cso"].includes(
                          (user?.role || "").toLowerCase());
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [guards, setGuards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState(["open", "in_progress", "resolved", "verified"]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [viewingPhotos, setViewingPhotos] = useState(null);
  const [filters, setFilters] = useState({ client_names: [], site_ids: [], guard_ids: [] });
  const bulk = useBulkSelection();
  const location = useLocation();
  const isFs = useFullscreenRoute(location.pathname);
  useRememberWindowGeometry(isFs ? location.pathname : null);

  const load = useCallback(async () => {
    const [a, b, c, cats] = await Promise.all([
      api.get("/maintenance-requests"),
      api.get("/sites"),
      api.get("/guards"),
      api.get("/portal/maintenance-categories"),
    ]);
    setItems(a.data);
    setSites(b.data);
    setGuards(c.data);
    setCategories(cats.data.categories || []);
    setStatuses(cats.data.statuses || statuses);
  }, [statuses]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    if (!form.site_id || !form.title) { toast.error("Site & title required"); return; }
    try {
      const payload = { ...form, guard_id: form.guard_id || null };
      if (editing) await api.put(`/maintenance-requests/${editing.id}`, payload);
      else await api.post("/maintenance-requests", payload);
      toast.success(editing ? "Updated" : "Maintenance request logged");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(errorMessage(e, "Save failed")); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete maintenance request?")) return;
    try {
      await api.delete(`/maintenance-requests/${id}`);
      toast.success("Removed"); load();
    } catch (e) { toast.error(errorMessage(e, "Delete failed")); }
  };

  const onEdit = (i) => {
    setEditing(i);
    setForm({
      ...empty,
      ...i,
      guard_id: i.guard_id || "",
      photo_attachments: i.photo_attachments || [],
    });
    setOpen(true);
  };

  const sevTone = (s) => ({ critical: "red", high: "red", medium: "amber", low: "blue" }[s] || "default");
  const statTone = (s) => ({ open: "red", in_progress: "amber", resolved: "blue", verified: "green" }[s] || "default");

  const filtered = useMemo(() => {
    const { client_names, site_ids, guard_ids } = filters;
    if (!client_names.length && !site_ids.length && !guard_ids.length) return items;
    const cset = new Set(client_names), sset = new Set(site_ids), gset = new Set(guard_ids);
    return items.filter((i) =>
      (cset.size === 0 || cset.has(i.client_name)) &&
      (sset.size === 0 || sset.has(i.site_id)) &&
      (gset.size === 0 || (i.guard_id && gset.has(i.guard_id)))
    );
  }, [items, filters]);

  // Only offer sites where maintenance reports are enabled (defaults true).
  const siteOptions = useMemo(() => sites
    .filter((s) => s.enabled_reports?.maintenance !== false)
    .map((s) => ({ value: s.id, label: s.name, sub: s.client_name || "" }))
    .sort((a, b) => a.label.localeCompare(b.label)),
  [sites]);

  const guardOptions = useMemo(() => guards
    .filter((g) => (g.status || "active") !== "inactive" && !g.deactivation_date)
    .map((g) => ({ value: g.id, label: g.name, sub: g.badge_no || "" }))
    .sort((a, b) => a.label.localeCompare(b.label)),
  [guards]);

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.value, label: c.label })),
    [categories],
  );

  const hasFilters = !!(filters.client_names.length || filters.site_ids.length || filters.guard_ids.length);

  return (
    <div>
      <PageHeader
        title="Maintenance Requests"
        subtitle="Track building maintenance issues flagged by  during rounds and by supervisors after site visits."
      />
      <div className="p-4 md:p-8 space-y-4">
        <StickyFiltersBar
          testIdPrefix="maintenance"
          value={filters}
          onChange={setFilters}
          visibleCount={filtered.length}
          totalCount={items.length}
        />
        <BulkResendBar
          kind="maintenance"
          selected={bulk.selected}
          onClear={bulk.clear}
          testIdPrefix="maintenance"
        />
        <div className="flex justify-end">
          <ActionButton data-testid="maintenance-new" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}>
            <Plus className="size-4 mr-2" /> New Maintenance Request
          </ActionButton>
        </div>

        <div className="hidden md:block border border-[#27272A] bg-[#121212] overflow-x-auto">
          <table className="w-full text-sm" data-testid="maintenance-table">
            <thead className="border-b border-[#27272A]">
              <tr>
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    data-testid="maintenance-select-all"
                    className="size-4 accent-[#0A84FF] cursor-pointer"
                    title="Select all visible rows"
                    checked={filtered.length > 0 && filtered.every((i) => bulk.isSelected(i.id))}
                    onChange={(e) => {
                      if (e.target.checked) bulk.selectAll(filtered.map((i) => i.id));
                      else bulk.clear();
                    }}
                  />
                </th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Logged</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Title</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Category</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Site</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Severity</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Status</div></th>
                <th className="px-4 py-3 text-left"><div className="label-mute">Reported by</div></th>
                <th className="px-4 py-3 text-right"><div className="label-mute">Actions</div></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-16 text-zinc-500 scanlines">
                  {hasFilters ? "No maintenance requests match the current filters." : "No maintenance requests yet."}
                </td></tr>
              )}
              {filtered.map((i) => {
                const catLabel = categories.find((c) => c.value === i.category)?.label || i.category;
                return (
                  <tr key={i.id} className="hover:bg-[#1c1c1e]" data-testid={`maintenance-row-${i.id}`}>
                    <td className="px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        data-testid={`maintenance-select-${i.id}`}
                        className="size-4 accent-[#0A84FF] cursor-pointer"
                        checked={bulk.isSelected(i.id)}
                        onChange={() => bulk.toggle(i.id)}
                      />
                    </td>
                    <td className="px-4 py-3 text-zinc-400 font-mono text-xs whitespace-nowrap">
                      {fmtDateTime(i.created_at, i.site_timezone)}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="font-medium text-white truncate">{i.title}</div>
                      {i.description && <div className="text-xs text-zinc-500 line-clamp-2">{i.description}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <PillBadge>{catLabel || "—"}</PillBadge>
                    </td>
                    <td className="px-4 py-3 text-zinc-300 text-xs">
                      {i.site_name || "—"}
                      {i.client_name && <div className="text-[10px] text-zinc-600 font-mono">{i.client_name}</div>}
                    </td>
                    <td className="px-4 py-3"><PillBadge tone={sevTone(i.severity)}>{i.severity}</PillBadge></td>
                    <td className="px-4 py-3">
                      {i.status
                        ? <PillBadge tone={statTone(i.status)}>{i.status.replace("_", " ")}</PillBadge>
                        : <span className="text-zinc-600 text-xs italic" data-testid={`maintenance-status-unset-${i.id}`}>not set</span>}
                      {i.approval_status && (
                        <div className="mt-1"><ApprovalPill status={i.approval_status} testId={`maintenance-approval-pill-${i.id}`} /></div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{i.reported_by_name || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        {(i.photo_attachments || []).length > 0 && (
                          <GhostButton onClick={() => setViewingPhotos(i.photo_attachments)} data-testid={`maintenance-photos-${i.id}`}>
                            📎 {i.photo_attachments.length}
                          </GhostButton>
                        )}
                        <AdminCommentsButton kind="maintenance" targetId={i.id} targetTitle={i.title} />
                        <GhostButton onClick={() => onEdit(i)} data-testid={`maintenance-edit-${i.id}`}>Edit</GhostButton>
                        <ResendReportEmailButton kind="maintenance" itemId={i.id} testId={`maintenance-resend-email-${i.id}`} />
                        <GhostButton onClick={() => onDelete(i.id)} data-testid={`maintenance-delete-${i.id}`}>
                          <Trash2 className="size-3.5" />
                        </GhostButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile — card stack of the same rows */}
        <div className="md:hidden space-y-2" data-testid="maintenance-mobile-list">
          {filtered.length === 0 && (
            <div className="border border-[#27272A] bg-[#121212] p-8 text-center text-zinc-500 scanlines">
              {hasFilters ? "No maintenance requests match the current filters." : "No maintenance requests yet."}
            </div>
          )}
          {filtered.map((i) => {
            const catLabel = categories.find((c) => c.value === i.category)?.label || i.category;
            return (
              <div
                key={i.id}
                data-testid={`maintenance-mobile-card-${i.id}`}
                className="border border-[#27272A] bg-[#121212] p-3 space-y-2"
              >
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    data-testid={`maintenance-mobile-select-${i.id}`}
                    className="mt-1 size-4 accent-[#0A84FF] cursor-pointer shrink-0"
                    checked={bulk.isSelected(i.id)}
                    onChange={() => bulk.toggle(i.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-white break-words">{i.title}</div>
                    {i.description && <div className="text-xs text-zinc-500 line-clamp-2 mt-0.5">{i.description}</div>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <PillBadge tone={sevTone(i.severity)}>{i.severity}</PillBadge>
                  {i.status
                    ? <PillBadge tone={statTone(i.status)}>{i.status.replace("_", " ")}</PillBadge>
                    : <span className="text-zinc-600 text-[10px] italic uppercase tracking-widest">not set</span>}
                  <PillBadge>{catLabel || "—"}</PillBadge>
                  {i.approval_status && <ApprovalPill status={i.approval_status} testId={`maintenance-mobile-approval-${i.id}`} />}
                </div>
                <div className="text-xs text-zinc-400 space-y-0.5">
                  <div><span className="text-zinc-600 uppercase tracking-widest text-[9px] font-mono">Site · </span>{i.site_name || "—"}{i.client_name ? ` (${i.client_name})` : ""}</div>
                  <div><span className="text-zinc-600 uppercase tracking-widest text-[9px] font-mono">Logged · </span><span className="font-mono">{fmtDateTime(i.created_at, i.site_timezone)}</span></div>
                  <div><span className="text-zinc-600 uppercase tracking-widest text-[9px] font-mono">By · </span>{i.reported_by_name || "—"}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-[#1f1f22]">
                  {(i.photo_attachments || []).length > 0 && (
                    <GhostButton onClick={() => setViewingPhotos(i.photo_attachments)} data-testid={`maintenance-mobile-photos-${i.id}`}>
                      📎 {i.photo_attachments.length}
                    </GhostButton>
                  )}
                  <AdminCommentsButton kind="maintenance" targetId={i.id} targetTitle={i.title} />
                  <GhostButton onClick={() => onEdit(i)} data-testid={`maintenance-mobile-edit-${i.id}`}>Edit</GhostButton>
                  <ResendReportEmailButton kind="maintenance" itemId={i.id} testId={`maintenance-mobile-resend-${i.id}`} />
                  <GhostButton onClick={() => onDelete(i.id)} data-testid={`maintenance-mobile-delete-${i.id}`}>
                    <Trash2 className="size-3.5" />
                  </GhostButton>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {open && (
      <Modal
        onClose={() => { setOpen(false); setEditing(null); setForm(empty); }}
        title={editing ? "Edit maintenance request" : "New maintenance request"}
        testId="maintenance-form-modal"
      >
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Site">
            <SingleSelectDropdown
              testId="maintenance-site"
              options={siteOptions}
              value={form.site_id}
              onChange={(v) => set("site_id", v)}
              searchable
            />
          </FormField>
          <FormField label="Reported by (, optional)">
            <SingleSelectDropdown
              testId="maintenance-guard"
              options={[{ value: "", label: "— none —" }, ...guardOptions]}
              value={form.guard_id}
              onChange={(v) => set("guard_id", v)}
              searchable
            />
          </FormField>
          <div className="col-span-2">
            <FormField label="Title">
              <input data-testid="maintenance-title" className={inputClass} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Broken pipe in restroom" />
            </FormField>
          </div>
          <FormField label="Category">
            <select data-testid="maintenance-category" className={inputClass} value={form.category} onChange={(e) => set("category", e.target.value)}>
              {categoryOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </FormField>
          <FormField label="Severity">
            <select data-testid="maintenance-severity" className={inputClass} value={form.severity} onChange={(e) => set("severity", e.target.value)}>
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
            </select>
          </FormField>
          {canEditStatus && (
            <FormField label="Status">
              <select
                data-testid="maintenance-status"
                className={inputClass}
                value={form.status ?? ""}
                onChange={(e) => set("status", e.target.value || null)}
              >
                <option value="">— none —</option>
                {statuses.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 block">
                Leave &ldquo;none&rdquo; until you&rsquo;re ready to triage.
              </span>
            </FormField>
          )}
          <FormField label="Location on site">
            <input data-testid="maintenance-location" className={inputClass} placeholder="e.g. restroom stall #2" value={form.location_on_site} onChange={(e) => set("location_on_site", e.target.value)} />
          </FormField>
          <div className="col-span-2">
            <FormField label="Description">
              <textarea data-testid="maintenance-description" rows={4} className={inputClass} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What's broken? When did you notice it? Is it safe to leave until scheduled repair?" />
            </FormField>
          </div>
          <div className="col-span-2">
            <FormField label="Action taken (if any)">
              <textarea data-testid="maintenance-action" rows={2} className={inputClass} value={form.action_taken} onChange={(e) => set("action_taken", e.target.value)} placeholder="e.g. shut off water valve; posted 'do not use' sign" />
            </FormField>
          </div>
          <div className="col-span-2">
            <label className="flex items-center gap-2 cursor-pointer select-none text-sm">
              <input type="checkbox" data-testid="maintenance-followup" className="accent-[#FF3B30]" checked={!!form.follow_up_needed} onChange={(e) => set("follow_up_needed", e.target.checked)} />
              <span className="text-zinc-300">Follow-up required</span>
            </label>
          </div>
          <div className="col-span-2">
            <FormField label="Photos">
              <PhotoAttachmentsField
                value={form.photo_attachments}
                onChange={(v) => set("photo_attachments", v)}
                purpose="maintenance"
                site={sites.find((s) => s.id === form.site_id) || null}
                allowVideo
                testIdPrefix="mr-photo"
              />
            </FormField>
          </div>
          {editing?.approval_status && (
            <div className="col-span-2">
              <ApprovalActions kind="maintenance" report={editing} onChange={() => { load(); setOpen(false); setEditing(null); setForm(empty); }} />
            </div>
          )}
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t border-[#27272A]">
            <GhostButton onClick={() => { setOpen(false); setEditing(null); setForm(empty); }}>Cancel</GhostButton>
            <ActionButton onClick={onSave} data-testid="maintenance-save">{editing ? "Save" : "Log request"}</ActionButton>
          </div>
        </div>
      </Modal>
      )}

      {viewingPhotos && (
        <Modal onClose={() => setViewingPhotos(null)} title="Attachments" testId="maintenance-photos-modal">
          <AttachmentViewer items={viewingPhotos} testIdPrefix="maintenance-attachment" />
        </Modal>
      )}
    </div>
  );
}
